/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  RefObject
} from 'react'
import { Button, Menu, message, Divider, Input, Dropdown } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import CONFIG from '../../../constants/config'
import {
  setFilter,
  fetchPhotos,
  resetFilter,
  setQueryFilter,
  setLastPageCond,
  fetchRecommendSearchKeywords
} from '../../../actions/photo'
import { fetchGroupInfo } from '../../../actions/group'
import sensors, { sensorsRole } from '../../../utils/sensors'
import DatesPicker from '../../components/DatesPickerV2'
import WatermarksPicker from '../../components/WatermarksPicker'
import DepartmentMemberPicker from '../../components/DepartmentMemberPickerV2'
import LocationPicker from '../../components/LocationPicker'
import Keywords from '../../components/Keywords'
import { capturePromiseException } from '../../../utils/sentryUtils'
import actionTypes from '../../../constants/actionTypes'
import { setLoading } from '../../../actions/main'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import { withSearchHistory } from '../../../app/hooks/useSearchKeywordsHistory'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  CalendarOutlined,
  CaretDownOutlined,
  RightOutlined
} from '@ant-design/icons'
import type { Grid } from 'react-virtualized'

const { SubMenu } = Menu

interface FilterProps {
  photoListRef: RefObject<Grid>
  handleSensorOfClickEvent: (
    clickItem: string,
    restProperties?: Record<string, any>
  ) => void
  keywordsHistory?: {
    addKeywords: (keywords: any) => void
  }
}

const quickPickerItems = [
  {
    text: '今天',
    clickItem: 'today',
    startTime: () => dayjs().startOf('day'),
    endTime: () => dayjs().endOf('day')
  },
  {
    text: '昨天',
    clickItem: 'yesterday',
    startTime: () => dayjs().add(-1, 'day').startOf('day'),
    endTime: () => dayjs().add(-1, 'day').endOf('day')
  },
  {
    text: '过去30天',
    clickItem: 'past30days',
    startTime: () => dayjs().add(-30, 'days').startOf('days'),
    endTime: () => dayjs().add(-1, 'days').endOf('days')
  }
]

const ACCEPTANCE_SEARCHED_KEY = 'acceptanceSearched'

function Filter({
  photoListRef,
  handleSensorOfClickEvent,
  keywordsHistory
}: FilterProps) {
  const dispatch = useAppDispatch()

  // Redux state - 直接访问属性，不使用 .get()
  const dates = useAppSelector((state) => state.photo.dates)
  const filter = useAppSelector((state) => state.photo.filter)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const lastPageCond = useAppSelector((state) => state.photo.lastPageCond)
  const groupId = useAppSelector((state) => state.group.groupId)
  const industryCode = useAppSelector((state) => state.group.industryCode)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const myRole = useAppSelector((state) => state.group.myRole)
  const viewPerm = useAppSelector((state) => state.group.viewPerm)
  const members = useAppSelector((state) => state.group.members)
  const lastSeq = useAppSelector((state) => state.photo.lastSeq)
  const isVip = useAppSelector((state) => state.group.isVip)
  const allWatermarksNum = useAppSelector(
    (state) => state.photo.allWatermarksNum
  )
  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  // 用 ref 保存最新 filter，避免 useCallback 闭包过期
  const filterRef = useRef(filter)
  filterRef.current = filter

  // Local state
  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showLocationPickerModal, setShowLocationPickerModal] = useState(false)
  const [onlyDisplayMine, setOnlyDisplayMine] = useState(true) // 普通成员只能展示自己拍摄的照片

  // Refs
  const moreFilterDropdown = useRef<HTMLDivElement>(null)
  const prevMyRoleRef = useRef(myRole)

  // componentDidMount + componentDidUpdate for myRole
  useEffect(() => {
    if (
      myRole === CONFIG.memberRoleTypes.ADMIN ||
      myRole === CONFIG.memberRoleTypes.MASTER_ADMIN
    ) {
      setOnlyDisplayMine(false)
    } else {
      setOnlyDisplayMine(true)
    }
  }, [myRole])

  const handleCancelChecked = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
  }, [dispatch])

  const handleCalcPickedMemberNum = useCallback(() => {
    const userNumInDepartments = filter.departments
      .map((department: any) => department.num)
      .reduce((acc: number, cur: number) => acc + cur, 0)
    const total = filter.departmentUsers.length + userNumInDepartments
    const hasSelection =
      filter.departmentUsers.length > 0 || filter.departments.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === pickerUserCount ||
      filter.departments.some((department: any) => department.id === '0')
    ) {
      return pickerUserCount
    } else {
      return total
    }
  }, [filter, pickerUserCount])

  const handleFetchPhotos = useCallback(() => {
    if (!queryFilter) return
    handleCancelChecked()

    const requestStartTime = new Date().getTime()
    const properties = {
      searchContent: queryFilter.searchKeywords,
      fileType:
        queryFilter.mediaType === null
          ? 'all'
          : queryFilter.mediaType === 0
            ? 'photo'
            : 'video',
      dateOffset: queryFilter.times[1].diff(queryFilter.times[0], 'days') + 1,
      startTime: queryFilter.times[0].format(CONFIG.timeFormat),
      endTime: queryFilter.times[1].format(CONFIG.timeFormat),
      memberNum: handleCalcPickedMemberNum(),
      isAllMember: handleCalcPickedMemberNum() === members.length,
      watermarkNum: queryFilter.watermarks.length,
      isAllWatermark:
        queryFilter.watermarks.length === 0 ||
        queryFilter.watermarks.length === allWatermarksNum,
      photoLocation:
        (queryFilter.locations[0] && queryFilter.locations[0].name) || '',
      role: sensorsRole(myRole),
      groupID: groupId
    }
    sensors.track('web_workgroup_picspage_click_filter', properties)

    let finalQueryFilter: any = queryFilter
    if (onlyDisplayMine) {
      // 因为照片查看接入了权限中心，但文件夹相关权限还不支持。特殊处理：非管理员时，只能看到自己的照片，和之前保持一致
      finalQueryFilter = {
        ...queryFilter,
        departmentUsers: [{ userID: userId }]
      }
    }

    dispatch(fetchPhotos(buildFetchPhotosParams(finalQueryFilter, groupId, '')))
      .then((result) => {
        sensors.track('web_workgroup_picspage_filter_suc', {
          processTime: new Date().getTime() - requestStartTime,
          firstPageNum: result.payload.total
        })
        photoListRef.current &&
          photoListRef.current.scrollToPosition({
            scrollLeft: 0,
            scrollTop: 0
          })
      })
      .catch(capturePromiseException)
  }, [
    dispatch,
    queryFilter,
    groupId,
    members.length,
    myRole,
    allWatermarksNum,
    onlyDisplayMine,
    userId,
    photoListRef,
    handleCancelChecked,
    handleCalcPickedMemberNum
  ])

  const handleDatesChange = useCallback(
    (startDate: Dayjs, endDate: Dayjs) => {
      setShowDatePickerPane(false)
      const newFilter = startDate.isAfter(endDate)
        ? { ...filterRef.current, times: [endDate, startDate] }
        : { ...filterRef.current, times: [startDate, endDate] }
      filterRef.current = newFilter
      dispatch(setFilter(newFilter))
    },
    [dispatch]
  )

  const handleLocationChange = useCallback(
    (location: any) => {
      setShowLocationPickerModal(false)
      const newFilter = { ...filterRef.current, locations: [location] }
      filterRef.current = newFilter
      dispatch(setFilter(newFilter))
    },
    [dispatch]
  )

  const handleMemberChangeV2 = useCallback(
    (params: {
      selectedDepartmentUsers: any[]
      selectedDepartments: any[]
    }) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      setShowMemberPickerModal(false)
      const newFilter = {
        ...filterRef.current,
        departments: selectedDepartments,
        departmentUsers: selectedDepartmentUsers
      }
      filterRef.current = newFilter
      dispatch(setFilter(newFilter))
    },
    [dispatch]
  )

  const handleSubmit = useCallback(() => {
    setShowDatePickerPane(false)
    setShowMemberPickerModal(false)
    setShowWatermarkModal(false)
    setShowLocationPickerModal(false)

    const currentFilter = filterRef.current
    dispatch(setLastPageCond(''))
    dispatch(setQueryFilter(currentFilter)).then(() => {
      // 直接用 currentFilter 构建请求，不依赖闭包中的 queryFilter
      handleCancelChecked()
      let finalQueryFilter: any = currentFilter
      if (onlyDisplayMine) {
        finalQueryFilter = {
          ...currentFilter,
          departmentUsers: [{ userID: userId }]
        }
      }
      dispatch(
        fetchPhotos(buildFetchPhotosParams(finalQueryFilter, groupId, ''))
      ).then(() => {
        photoListRef.current &&
          photoListRef.current.scrollToPosition({
            scrollLeft: 0,
            scrollTop: 0
          })
      })
    })
    if (currentFilter.searchKeywords.includes('验收')) {
      localStorage.setItem(ACCEPTANCE_SEARCHED_KEY, 'true')
    }

    handleSensorOfClickEvent('beginSearch')
    keywordsHistory?.addKeywords(currentFilter.searchKeywords)
  }, [
    dispatch,
    keywordsHistory,
    handleSensorOfClickEvent,
    handleCancelChecked,
    onlyDisplayMine,
    userId,
    groupId,
    photoListRef
  ])

  const handleReset = useCallback(() => {
    dispatch(setLastPageCond(''))
    dispatch(resetFilter()).then(handleFetchPhotos)
    handleSensorOfClickEvent('reset')
  }, [dispatch, handleFetchPhotos, handleSensorOfClickEvent])

  const handleFilterModals = useCallback(
    (type: string) => {
      if (type === 'time') {
        sensors.track('web_workgroup_filter_time_click', {
          pagePath: window.location.pathname
        })

        handleSensorOfClickEvent('choosePhotoDate')

        setShowDatePickerPane((prev) => !prev)
        setShowMemberPickerModal(false)
        setShowWatermarkModal(false)
        setShowLocationPickerModal(false)
      }
      if (type === 'member') {
        handleSensorOfClickEvent('choosePhotoMember')

        setShowMemberPickerModal((prev) => !prev)
        setShowDatePickerPane(false)
        setShowWatermarkModal(false)
        setShowLocationPickerModal(false)
      }
      if (type === 'watermark') {
        handleSensorOfClickEvent('chooseWatermark')

        setShowWatermarkModal((prev) => !prev)
        setShowDatePickerPane(false)
        setShowMemberPickerModal(false)
        setShowLocationPickerModal(false)
      }
      if (type === 'location') {
        handleSensorOfClickEvent('chooseLocation')

        setShowLocationPickerModal((prev) => !prev)
        setShowWatermarkModal(false)
        setShowDatePickerPane(false)
        setShowMemberPickerModal(false)
      }
    },
    [handleSensorOfClickEvent]
  )

  const handleWatermarksChange = useCallback(
    (checkedList: string[]) => {
      const ids = checkedList.map((id: string) => id.split('+'))
      sensors.track('web_workgroup_filter_watermark_page_click_done', {
        watermarkNum: checkedList.length,
        groupWatermarkNum: ids.filter((id) => id[1]).length,
        basicWatermarkNum: ids.filter((id) => id[0] !== '0' && id[1] === '')
          .length,
        noWatermarkNum: ids.filter((id) => id[0] === '0').length,
        pageType: 'photoAlbums'
      })
      setShowWatermarkModal(false)
      const newFilter = { ...filterRef.current, watermarks: checkedList }
      filterRef.current = newFilter
      dispatch(setFilter(newFilter))
    },
    [dispatch]
  )

  const handleCalcMemberPickedValue = useCallback(() => {
    const pickedMembersNum = handleCalcPickedMemberNum()
    if (pickedMembersNum === pickerUserCount) {
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }, [handleCalcPickedMemberNum, pickerUserCount])

  const handleCalcWatermarkPickedValue = useCallback(() => {
    return filter.watermarks.length === allWatermarksNum ||
      filter.watermarks.length === 0
      ? `全部(${allWatermarksNum}个)`
      : `${filter.watermarks.length}个水印`
  }, [filter, allWatermarksNum])

  // 计算更多筛选条件数量, 用于显示更多筛选条件的状态
  const handleFilterMoreCount = useCallback(() => {
    let count = 0
    if (filter.locations.length > 0) {
      count++
    }
    if (filter.mediaType !== null) {
      count++
    }
    return count
  }, [filter])

  const renderTimePickerArea = () => {
    const startTime = filter.times[0]
    const endTime = filter.times[1]
    const isThisMonth =
      dayjs().startOf('month').isSame(startTime, 'second') &&
      dayjs().endOf('day').isSame(endTime, 'second')
    const isAllPhoto =
      startTime.isSame(dayjs('1970-01-01')) &&
      endTime.isSame(dayjs().endOf('days'))
    const startTimeFormated = dayjs().isSame(startTime, 'year')
      ? startTime.format('M月D日')
      : startTime.format('YY年M月D日')
    const endTimeFormated = dayjs().isSame(endTime, 'year')
      ? endTime.format('M月D日')
      : endTime.format('YY年M月D日')

    return (
      <div className="time-wrapper">
        <p className="label">筛选时间</p>
        <p
          className="timepicker-wrapper"
          onClick={() => handleFilterModals('time')}
        >
          {isThisMonth && <span className="this-month">本月 丨 </span>}
          {isAllPhoto ? (
            <span className="this-month">所有照片</span>
          ) : (
            startTimeFormated + '-' + endTimeFormated
          )}
          <CalendarOutlined />
        </p>
        <p className="quickpicker-wrapper">
          快速筛选{' '}
          {quickPickerItems.map((item, index) => {
            return (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  handleDatesChange(item.startTime(), item.endTime())
                  handleSensorOfClickEvent('quickChooseTime', {
                    clickQuickTimeContent: item.text
                  })
                }}
                key={index}
              >
                {item.text}
              </Button>
            )
          })}
        </p>
        <DatesPicker
          visible={showDatePickerPane}
          dates={filter.times}
          onChange={handleDatesChange}
          onCancel={() => setShowDatePickerPane(false)}
        />
      </div>
    )
  }

  const renderKeywordsArea = () => {
    return (
      <Keywords
        value={filter.searchKeywords}
        onChange={(value) => {
          dispatch(setFilter({ ...filter, searchKeywords: value }))
        }}
        onSensorOfClickEvent={handleSensorOfClickEvent}
        onSubmit={handleSubmit}
      />
    )
  }

  const filterMoreMenu = (
    <Menu>
      <Menu.Item onClick={() => handleFilterModals('location')}>
        <span>筛选地点：{filter.locations.length > 0 ? '已选择' : '不限'}</span>
        <RightOutlined />
      </Menu.Item>
      <SubMenu
        title={
          <span>
            {`内容类型：${
              filter.mediaType === null
                ? '全部'
                : filter.mediaType === 0
                  ? '仅照片'
                  : '仅视频'
            }`}
          </span>
        }
      >
        <Menu.Item
          onClick={() => {
            dispatch(setFilter({ ...filter, mediaType: null }))
            handleSensorOfClickEvent('chooseFileType')
          }}
        >
          全部类型
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            dispatch(setFilter({ ...filter, mediaType: 0 }))
            handleSensorOfClickEvent('chooseFileType')
          }}
        >
          照片
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            dispatch(setFilter({ ...filter, mediaType: 1 }))
            handleSensorOfClickEvent('chooseFileType')
          }}
        >
          视频
        </Menu.Item>
      </SubMenu>
    </Menu>
  )

  const filterMoreNum = handleFilterMoreCount()

  return (
    <div className="photo-filter-wrapper">
      <div className="time-and-keywords-wrapper">
        {renderTimePickerArea()}
        <Divider
          orientation="vertical"
          style={{ height: '52px', margin: '8px 17px' }}
        />
        {renderKeywordsArea()}
      </div>
      <div className="other-filter-wrapper" ref={moreFilterDropdown}>
        {!onlyDisplayMine && (
          <>
            <div
              className="member-filter-dropdown"
              onClick={() => handleFilterModals('member')}
            >
              <span className="label">筛选人员</span>
              <span className="value">{handleCalcMemberPickedValue()}</span>
              <CaretDownOutlined />
            </div>
            <Divider
              orientation="vertical"
              style={{ height: '26px', margin: '0 16px' }}
            />
          </>
        )}

        <div
          className="member-filter-dropdown"
          onClick={() => handleFilterModals('watermark')}
        >
          <span className="label">筛选水印</span>
          <span className="value">{handleCalcWatermarkPickedValue()}</span>
          <CaretDownOutlined />
        </div>
        <Divider
          orientation="vertical"
          style={{ height: '26px', margin: '0 16px' }}
        />
        <Dropdown
          popupRender={() => filterMoreMenu}
          classNames={{ root: 'filter-more-menu' }}
          getPopupContainer={() => moreFilterDropdown.current!}
        >
          <div
            className={`more-filter-dropdown ${
              filterMoreNum && 'more-filtered'
            }`}
          >
            <span className="label">
              更多筛选{filterMoreNum > 0 && `(${filterMoreNum})`}
            </span>
            <CaretDownOutlined />
          </div>
        </Dropdown>

        <DepartmentMemberPicker
          open={showMemberPickerModal}
          filterObj={filter as any}
          onOk={handleMemberChangeV2 as any}
          onCancel={() => setShowMemberPickerModal(false)}
        />
        <WatermarksPicker
          visible={showWatermarkModal}
          onOK={handleWatermarksChange}
          onCancel={() => setShowWatermarkModal(false)}
          checkedList={filter.watermarks}
          fetchWatermarkList={true as any}
        />
        <LocationPicker
          visible={showLocationPickerModal}
          onCancel={() => setShowLocationPickerModal(false)}
          groupId={groupId}
          handleCheckLocation={handleLocationChange}
        />
      </div>
      <Button size="large" style={{ marginLeft: '20px' }} onClick={handleReset}>
        重置
      </Button>
      <Button
        size="large"
        type="primary"
        style={{ width: '145px', marginLeft: '12px' }}
        onClick={handleSubmit}
      >
        筛选
      </Button>
    </div>
  )
}

export default withSearchHistory(Filter)
